import React from 'react';

import { FormattedMessage } from '@99designs/common/i18n';
import { TimeAndBudgetPage_Site } from '__generated__/TimeAndBudgetPage_Site';
import { TimeAndBudgetPage_StructuredBrief } from '__generated__/TimeAndBudgetPage_StructuredBrief';
import { ProjectTimeframe } from '__generated__/types';
import Responsive from 'common/components/Responsive';
import { handlePromise, onReject } from 'common/utils/Bugsnag';
import { Currency, toUnit } from 'common/utils/currency';
import debounce from 'lodash/debounce';
import BudgetSlider from 'projects/components/BudgetSlider';
import ColorComponent from 'projects/components/Color';
import DateRange from 'projects/components/DateRange';
import FadeIn from 'projects/components/FadeIn';
import {
    ObservableField as Field,
    ObservableForm,
    Observe,
} from 'projects/components/ObservableForm';
import TimeFrame from 'projects/components/TimeFrame';
import { Color } from 'projects/consts';
import { trackBudgetEvent, trackViewedBriefEvent } from 'projects/events';
import { intlShape } from 'react-intl';

type GetFunctionProps<T> = T extends (props: infer U) => any ? U : never;
type trackBudgetEventParams = GetFunctionProps<typeof trackBudgetEvent>;
type inputType = trackBudgetEventParams['input_type'];

interface Fields {
    timeframe: ProjectTimeframe;
    startDate: any | null;
    endDate: any | null;
    budget: number;
    currency: string;
}

export interface Props<T> {
    form: ObservableForm<T>;
    brief: TimeAndBudgetPage_StructuredBrief;
    site: TimeAndBudgetPage_Site;
}

class TimeAndBudgetPage<T extends Fields> extends React.Component<Props<T>> {
    debouncedEvent: (inputType: inputType) => void;

    static contextTypes = {
        intl: intlShape.isRequired,
    };

    constructor(props: Props<T>) {
        super(props);

        this.debouncedEvent = debounce(
            inputType =>
                trackBudgetEvent({
                    id: this.props.brief.id,
                    input_type: inputType,
                }),
            1000
        );
    }

    componentDidMount() {
        trackViewedBriefEvent({
            id: this.props.brief.id,
            page: 'TimeAndBudget',
        });
    }

    // FIXME: Handle lowercase enum value
    get timeframe() {
        return this.props.form.get('timeframe').toUpperCase();
    }

    renderPriceGuide() {
        const category = this.props.brief.category;

        if (!category) {
            return;
        }

        const { currency } = this.props.site;
        const { formatNumber } = this.context.intl;
        const formattedPriceRange = category.priceRange
            .map(price =>
                formatNumber(toUnit(price, currency as Currency), {
                    style: 'currency',
                    currency: currency,
                    minimumFractionDigits: 0,
                })
            )
            .join(' - ');

        return (
            <FormattedMessage
                message={__(
                    'Tip: For projects in the {category} category, designers typically charge between {range}.'
                )}
                values={{
                    category: <strong>{category.title}</strong>,
                    range: <span className="price-range">{formattedPriceRange}</span>,
                }}
            />
        );
    }

    renderChooseDates(title: string) {
        const dateField = (
            <Field
                form={this.props.form}
                name={['startDate', 'endDate']}
                title={title}
                render={() => (
                    <>
                        <Responsive min="normal">
                            <DateRange
                                startDate={this.props.form.get('startDate')}
                                endDate={this.props.form.get('endDate')}
                                onChange={(name, value) => {
                                    handlePromise(
                                        this.props.form.set(name, value),
                                        'TimeAndBudgetPage'
                                    );
                                }}
                                orientation="horizontal"
                            />
                        </Responsive>
                        <Responsive max="medium">
                            <DateRange
                                startDate={this.props.form.get('startDate')}
                                endDate={this.props.form.get('endDate')}
                                onChange={(name, value) => {
                                    handlePromise(
                                        this.props.form.set(name, value),
                                        'TimeAndBudgetPage'
                                    );
                                }}
                                orientation="vertical"
                            />
                        </Responsive>
                    </>
                )}
            />
        );

        return (
            <FadeIn wrapperComponent="div" wrapperClassName="wrapped-card">
                {this.timeframe == ProjectTimeframe.SPECIFIC_DATES && dateField}
            </FadeIn>
        );
    }

    onSliderChange = (budget: number, inputType: inputType) => {
        handlePromise(Promise.all([this.props.form.set('budget', budget)]), 'TimeAndBudgetPage');

        this.debouncedEvent(inputType);
    };

    render() {
        const currency = this.props.form.get('currency')
            ? (this.props.form.get('currency') as Currency)
            : (this.props.site.currency as Currency);

        return (
            <div className="content-setting">
                <Field
                    form={this.props.form}
                    name="timeframe"
                    title={__("What's your timeline?")}
                    message={__(
                        'Your designer will confirm whether your delivery date can be met based on project scope and their availability.'
                    )}
                    render={() => (
                        <ColorComponent color={Color.createBlue}>
                            <TimeFrame
                                value={this.timeframe}
                                onChange={async value => {
                                    try {
                                        await this.props.form.set('timeframe', value);
                                    } catch {
                                        onReject('TimeAndBudgetPage');
                                    } finally {
                                        if (value === ProjectTimeframe.SPECIFIC_DATES) {
                                            this.props.form.scrollTo('startDate');
                                        }
                                    }
                                }}
                            />
                        </ColorComponent>
                    )}
                />

                <Observe
                    form={this.props.form}
                    name="timeframe"
                    render={() => this.renderChooseDates(__('Choose a start & end date'))}
                />

                <Field
                    form={this.props.form}
                    name={['budget']}
                    title={__("What's your estimated budget?")}
                    message={__(
                        "Your designer will confirm whether your budget range can be met based on the designer's level, experience, scope and complexity of your project."
                    )}
                    render={() => (
                        <>
                            <BudgetSlider
                                budget={this.props.form.get('budget')}
                                onSliderChange={this.onSliderChange}
                                currency={currency}
                            />
                            {this.renderPriceGuide()}
                        </>
                    )}
                />
            </div>
        );
    }
}

export default TimeAndBudgetPage;
